import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { ThemeContext } from 'styled-components';
import styled from 'styled-components';
import { UnstyledButton, IconButton } from '../common/Button';
import { useNavigate } from 'react-router-dom';
import { feedService } from '../../services/feedService';
import { MediaModal } from '../common/MediaPlayer';
import { ConfirmModal } from '../common/Modal';
import { useAuth } from '../../context/AuthContext';
import VipIcon from '../Icons/VipIcons';
import UserTaggingModal from '../TagInput/UserTaggingModal';
import CategoryPickerModal from '../Category/CategoryPickerModal';
import Mention from '@tiptap/extension-mention';
import { smartMentionSuggestion } from '../Editor/smartMentionConfig';
import { toast } from '../../utils/notify';
import { validateText } from '../../utils/contentFilter';
import { MediaUploader } from '../common';
import { formatMediaForCarousel } from '../common/MediaPlayer/exports';
import { useDraftPost } from '../../hooks/useDraftPost';
import DateTimePickerModal from '../common/DateTimePickerModal';
import { SCHEDULED_PAGE_URL } from '../../constants/systemConstants';
import {
  Box,
  Flex,
  ProfileImage,
  TaggedCreators,
  Input,
  Tooltip
} from '../common';
import {
  DragIndicator,
  FormatToolbar,
  FormatButton,
  ComposeActions,
  MediaButtons,
  MediaButton,
  UploadGroup,
  ComposeButton,
  PollOption,
  AddOptionButton
} from './styles';

// Replace styled components with common components
const ComposeBox = styled(Box)`
  background: ${({theme}) => theme.colors.background};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: ${({ $isModal }) => $isModal ? '0' : '20px'};
  position: relative;

  @media (max-width: 480px) {
    padding: 12px;
  }

  /* Compact sizing for phones (scoped here; the referenced components are shared) */
  @media (max-width: 768px) {
    ${FormatToolbar} {
      gap: 4px;
      padding: 2px 4px;
      margin-bottom: 6px;
    }

    ${FormatButton} {
      width: 26px;
      height: 26px;
    }

    ${ComposeActions} {
      margin-top: 10px;
      padding-top: 10px;
    }

    ${MediaButtons} {
      gap: 4px;
    }

    ${MediaButton} {
      padding: 6px;

      svg {
        width: 18px;
        height: 18px;
      }
    }

    ${ComposeButton} {
      padding: 6px 16px;
      font-size: 13px;
    }
  }

  &.dragging {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 123, 255, 0.1);
      border: 2px dashed ${({theme}) => theme.colors.primary};
      border-radius: 16px;
      z-index: 10;
      pointer-events: none;
    }
  }
`;

const ComposeInput = styled(Box)`
  flex: 1;
  width: 100%;
  min-width: 0; /* Prevents flex item from overflowing */
  
  .ProseMirror {
    outline: none;
    min-height: 80px;
    padding: 12px 16px;
    border: 1px solid ${({theme}) => theme.colors.divider};
    border-radius: 12px;
    background: transparent;
    color: ${({theme}) => theme.colors.text};
    font-size: ${({theme}) => theme.fontSize?.main || '14px'};
    
    /* Mobile Safari zoom prevention */
    @media (max-width: 768px) {
      font-size: 16px; /* Minimum 16px to prevent iOS Safari zoom */
      min-height: 64px;
      padding: 8px 12px;
    }
    
    &:focus {
      outline: none;
      border-color: ${({theme}) => theme.colors.primary};
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: ${({theme}) => theme.colors.textSecondary};
      pointer-events: none;
      height: 0;
    }

    pre {
      background: ${({theme}) => theme.colors.bg3};
      border-radius: 4px;
      padding: 8px 12px;
      font-family: monospace;
    }

    ul, ol {
      padding-left: 24px;
      margin: 0.5em 0;
    }

    blockquote {
      border-left: 3px solid ${({theme}) => theme.colors.primary};
      padding-left: 12px;
      color: ${({theme}) => theme.colors.textSecondary};
      margin: 1em 0;
    }

    strong {
      font-weight: 600;
    }

    em {
      font-style: italic;
    }

    * {
      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

// Tagged Creators Display Components - Using common component

// Styled common components for audience selection
const AudienceContainer = styled(Flex)`
  margin-top: 16px;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    margin-top: 10px;
    gap: 10px;
  }
`;

const AudienceLabel = styled(Box)`
  font-size: 14px;
  @media (max-width: 768px) {
    font-size: 13px;
  }
  font-weight: 500;
  color: ${({theme}) => theme.colors.primaryDark};
  min-width: fit-content;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AudienceOptionsContainer = styled(Flex)`
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const AudienceOption = styled(Flex)`
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${({theme}) => theme.colors.primaryDark};
  transition: opacity 0.2s ease;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ValidationMessage = styled(Box)`
  font-size: 12px;
  color: ${({theme}) => theme.colors.error || '#ef4444'};
  font-style: italic;
`;

const ComposeRow = styled(Flex)`
  margin-bottom: 24px;

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
`;

const CloseButton = styled(IconButton).attrs({ $size: 'small' })`
  color: ${({theme}) => theme.colors?.textSecondary || '#888'};
`;

const CreatePost = ({ 
  onPostCreated, 
  defaultPostType = 'POST', 
  isOpen, 
  onClose,
  onRequestClose,
  showHeader = false,
  isEditing = false,
  initialPost = null,
  isSubmitting: externalIsSubmitting = false,
  submitButtonText = 'Post'
}) => {
  const theme = useContext(ThemeContext);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Helper to check accessSpec permissions
  const hasAccess = (feature) => {
    if (!currentUser?.accessSpec) {
      return currentUser?.creator ?? false;
    }
    return currentUser.accessSpec[feature] ?? false;
  };
  const [content, setContent] = useState(isEditing ? (initialPost?.content || '') : '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postType, setPostType] = useState(isEditing ? (initialPost?.postType || defaultPostType) : defaultPostType);
  const [price, setPrice] = useState(isEditing ? (initialPost?.price?.toString() || '') : '');
  const [priceError, setPriceError] = useState(''); // For price validation error
  const [targetAmount, setTargetAmount] = useState(isEditing ? (initialPost?.targetAmount?.toString() || '') : '');
  
  // Get post price limits from currentUser.pricingLimits or use defaults
  // Check for null, not truthiness: an admin-configured limit of 0 is valid.
  const postMinPrice = currentUser?.pricingLimits?.postMinPrice != null
    ? parseFloat(currentUser.pricingLimits.postMinPrice)
    : 2.99;
  const postMaxPrice = currentUser?.pricingLimits?.postMaxPrice != null
    ? parseFloat(currentUser.pricingLimits.postMaxPrice)
    : 150.00;
  
  // Validate price on change
  const handlePriceChange = (newPrice) => {
    setPrice(newPrice);
    setPriceError(''); // Clear error while typing
    
    // Validate after user finishes typing (only if there's a value)
    if (newPrice && newPrice !== '') {
      const priceValue = parseFloat(newPrice);
      if (!isNaN(priceValue) && priceValue > 0) {
        if (priceValue < postMinPrice) {
          setPriceError(`Minimum price is $${postMinPrice.toFixed(2)}`);
        } else if (priceValue > postMaxPrice) {
          setPriceError(`Maximum price is $${postMaxPrice.toFixed(2)}`);
        }
      }
    }
  };
  
  // Audience selection for POST type
  const [audienceSettings, setAudienceSettings] = useState(() => {
    if (isEditing && initialPost?.postType) {
      switch (initialPost.postType) {
        case 'SUBSCRIBERS_ONLY':
          return { isPublic: false, visibleToSubscribers: true, visibleToFollowers: false };
        case 'FOLLOWERS_ONLY':
          return { isPublic: false, visibleToSubscribers: false, visibleToFollowers: true };
        case 'POST':
          return { isPublic: false, visibleToSubscribers: true, visibleToFollowers: true };
        default: // PUBLIC, TARGET, POLL
          return { isPublic: true, visibleToSubscribers: false, visibleToFollowers: false };
      }
    }
    return { isPublic: true, visibleToSubscribers: false, visibleToFollowers: false };
  });
  
  const [isPoll, setIsPoll] = useState(isEditing ? initialPost?.postType === 'POLL' : false);
  const [pollOptions, setPollOptions] = useState(() => {
    if (isEditing && initialPost?.postType === 'POLL' && initialPost.pollOptions) {
      try {
        return typeof initialPost.pollOptions === 'string' 
          ? JSON.parse(initialPost.pollOptions) 
          : initialPost.pollOptions;
      } catch (e) {
        console.warn('Error parsing poll options:', e);
        return ['', ''];
      }
    }
    return ['', ''];
  });
  const [pollQuestion, setPollQuestion] = useState(isEditing ? (initialPost?.pollQuestion || '') : '');
  const [allowMultipleChoices, setAllowMultipleChoices] = useState(isEditing ? (initialPost?.allowMultipleChoices || false) : false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [systemTaggedCreators, setSystemTaggedCreators] = useState([]); // For system creator tagging
  const [showTagModal, setShowTagModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState(
    () => (isEditing && Array.isArray(initialPost?.categories) ? initialPost.categories.map(c => c.name) : [])
  );
  // When editing a post whose payload has no categories field, leave its categories untouched
  const categoriesKnown = !isEditing || Array.isArray(initialPost?.categories);
  const [isDragging, setIsDragging] = useState(false);
  const [showPriceSection, setShowPriceSection] = useState(() => isEditing && !!initialPost?.price); // For toggling price section visibility
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');

  // Track initial values for change detection in edit mode
  const initialValues = useMemo(() => {
    if (isEditing && initialPost) {
      // Handle poll options parsing safely
      let initialPollOptions = ['', ''];
      try {
        if (initialPost.pollOptions && typeof initialPost.pollOptions === 'string') {
          initialPollOptions = JSON.parse(initialPost.pollOptions);
        } else if (Array.isArray(initialPost.pollOptions)) {
          initialPollOptions = initialPost.pollOptions;
        }
      } catch (e) {
        console.warn('Error parsing poll options:', e);
        initialPollOptions = ['', ''];
      }

      return {
        content: initialPost.content || '',
        postType: initialPost.postType || defaultPostType,
        price: initialPost.price?.toString() || '',
        targetAmount: initialPost.targetAmount?.toString() || '',
        pollQuestion: initialPost.pollQuestion || '',
        pollOptions: initialPollOptions,
        allowMultipleChoices: initialPost.allowMultipleChoices || false,
        mediaIds: initialPost.media ? initialPost.media.map(m => m.id).sort() : [],
        categories: Array.isArray(initialPost.categories) ? initialPost.categories.map(c => c.name).sort() : []
      };
    }
    return null;
  }, [isEditing, initialPost, defaultPostType]);

  // Media uploader state
  const [mediaData, setMediaData] = useState(() => {
    if (isEditing && initialPost?.media) {
      // Format the media for MediaUploader compatibility
      const formattedMedia = formatMediaForCarousel(initialPost.media);
      return { items: formattedMedia, price: 0 };
    }
    return { items: [], price: 0 };
  });
  const [triggerFileUpload, setTriggerFileUpload] = useState(false);
  const [triggerLibrary, setTriggerLibrary] = useState(false);
  const [showMediaUploader, setShowMediaUploader] = useState(() => {
    // Show media uploader if editing and has existing media
    return isEditing && initialPost?.media && initialPost.media.length > 0;
  });
  const [resetMediaUploader, setResetMediaUploader] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: isEditing ? "Edit your post..." : "What's on your mind?"
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        renderLabel: ({ options, node }) => {
          // Prefer displayName (full name) over label (username)
          return `${options.suggestion.char}${node.attrs.displayName ?? node.attrs.label ?? node.attrs.id}`;
        },
        suggestion: smartMentionSuggestion,
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      setContent(editor.getHTML());
    }
  });

  // Draft caching hook
  const { saveDraft, loadDraft, clearDraft } = useDraftPost(isEditing);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false); // Track if current content is saved as draft

  // Check for saved draft on mount and auto-restore
  useEffect(() => {
    if (!isEditing && !draftLoaded && editor) {
      const draft = loadDraft();
      console.log('[Draft] Checking for draft on mount:', draft, 'defaultPostType:', defaultPostType);
      
      // Only restore draft if it matches the requested post type
      if (draft && draft.postType === defaultPostType) {
        // Auto-restore the draft
        console.log('[Draft] Auto-restoring draft (matching post type)...');
        
        // Restore text content
        if (draft.content) {
          editor.commands.setContent(draft.content);
          setContent(draft.content);
        }
        
        // Restore post type
        if (draft.postType) {
          setPostType(draft.postType);
          setIsPoll(draft.postType === 'POLL');
        }
        
        // Restore price
        if (draft.price) {
          setPrice(draft.price);
          setShowPriceSection(true);
        }
        
        // Restore target amount
        if (draft.targetAmount) {
          setTargetAmount(draft.targetAmount);
        }
        
        // Restore poll data
        if (draft.pollQuestion) {
          setPollQuestion(draft.pollQuestion);
        }
        if (draft.pollOptions && draft.pollOptions.length > 0) {
          setPollOptions(draft.pollOptions);
        }
        if (draft.allowMultipleChoices !== undefined) {
          setAllowMultipleChoices(draft.allowMultipleChoices);
        }
        
        // Restore media (only items that were uploaded to S3)
        if (draft.mediaItems && draft.mediaItems.length > 0) {
          console.log('[Draft] Restoring media items:', draft.mediaItems);
          const restoredMedia = draft.mediaItems.map(item => ({
            ...item,
            // Ensure display URL fields are set
            fileUrl: item.url || item.fileUrl,
            preview: item.thumbnailUrl || item.url || item.fileUrl,
            uploadedToS3: true,
            processed: true
          }));
          console.log('[Draft] Restored media:', restoredMedia);
          setMediaData({ items: restoredMedia, price: 0 });
          setShowMediaUploader(true);
        }
        
        // Restore tagged creators
        if (draft.taggedCreators && draft.taggedCreators.length > 0) {
          setSystemTaggedCreators(draft.taggedCreators);
        }

        // Restore categories
        if (Array.isArray(draft.categories) && draft.categories.length > 0) {
          setCategories(draft.categories);
        }

        // Restore scheduled time (only if still in the future); clear stale value from storage
        if (draft.scheduledFor) {
          if (new Date(draft.scheduledFor) > new Date()) {
            setScheduledFor(draft.scheduledFor);
          } else {
            // Expired — rewrite the draft without it so the stale time doesn't linger
            try {
              const cleaned = { ...draft, scheduledFor: '' };
              localStorage.setItem('vip_draft_post', JSON.stringify(cleaned));
            } catch (_) {}
          }
        }
        
        console.log('[Draft] Draft restored successfully');
        setDraftSaved(true); // Draft was restored, so it's "saved"
      } else if (draft) {
        console.log('[Draft] Draft exists but post type mismatch. Draft:', draft.postType, 'Requested:', defaultPostType);
      }
      setDraftLoaded(true);
    }
  }, [isEditing, draftLoaded, loadDraft, editor, defaultPostType]);

  // Check if there's content worth saving
  const hasUnsavedContent = useMemo(() => {
    const hasText = content && content.replace(/<[^>]*>/g, '').trim().length > 0;
    const hasMedia = Array.isArray(mediaData.items) && mediaData.items.length > 0;
    const hasPollData = postType === 'POLL' && pollQuestion.trim().length > 0;
    return hasText || hasMedia || hasPollData;
  }, [content, mediaData.items, postType, pollQuestion]);

  // Save to draft function
  const handleSaveDraft = useCallback(() => {
    saveDraft({
      content,
      postType,
      price,
      targetAmount,
      pollQuestion,
      pollOptions,
      allowMultipleChoices,
      mediaItems: mediaData.items,
      taggedCreators: systemTaggedCreators,
      categories,
      scheduledFor: scheduledFor || ''
    });
    setDraftSaved(true);
    toast.success('Draft saved');
  }, [saveDraft, content, postType, price, targetAmount, pollQuestion, pollOptions, allowMultipleChoices, mediaData.items, systemTaggedCreators, categories, scheduledFor]);

  // Track if user has made any changes after initial load
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
  const initialLoadCompleteRef = React.useRef(false);
  
  // Mark initial load as complete after draft is loaded
  useEffect(() => {
    if (draftLoaded && !initialLoadCompleteRef.current) {
      // Wait a tick for any restored content to settle
      setTimeout(() => {
        initialLoadCompleteRef.current = true;
      }, 100);
    }
  }, [draftLoaded]);

  // Reset draftSaved and track changes when content changes (after initial load)
  useEffect(() => {
    if (initialLoadCompleteRef.current) {
      setDraftSaved(false);
      setHasUserMadeChanges(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, postType, price, targetAmount, pollQuestion, pollOptions, allowMultipleChoices, mediaData.items, systemTaggedCreators, categories]);

  // State for discard confirmation modal
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // Handle close with confirmation
  const handleCloseWithConfirmation = useCallback(() => {
    // Only show confirmation if user has made changes AND content exists AND not already saved as draft
    if (!isEditing && hasUnsavedContent && hasUserMadeChanges && !draftSaved) {
      setShowDiscardModal(true);
    } else {
      onClose?.();
      onRequestClose?.();
    }
  }, [isEditing, hasUnsavedContent, hasUserMadeChanges, draftSaved, onClose, onRequestClose]);

  // Handle discard confirmation
  const handleDiscardConfirm = useCallback(() => {
    clearDraft();
    setShowDiscardModal(false);
    onClose?.();
    onRequestClose?.();
  }, [clearDraft, onClose, onRequestClose]);

  // Change detection function for edit mode
  const hasChanges = useMemo(() => {
    if (!isEditing || !initialValues) return true; // For new posts, always allow submission
    
    const currentMediaIds = Array.isArray(mediaData.items) 
      ? mediaData.items.map(item => item.id || item.mediaId).filter(Boolean).sort()
      : [];
    
    const currentValues = {
      content: content || '',
      postType: postType || defaultPostType,
      price: price || '',
      targetAmount: targetAmount || '',
      pollQuestion: pollQuestion || '',
      pollOptions: pollOptions || ['', ''],
      allowMultipleChoices: allowMultipleChoices || false,
      mediaIds: currentMediaIds,
      categories: [...categories].sort()
    };

    // Deep comparison of all relevant fields
    return (
      currentValues.content !== initialValues.content ||
      currentValues.postType !== initialValues.postType ||
      currentValues.price !== initialValues.price ||
      currentValues.targetAmount !== initialValues.targetAmount ||
      currentValues.pollQuestion !== initialValues.pollQuestion ||
      JSON.stringify(currentValues.pollOptions) !== JSON.stringify(initialValues.pollOptions) ||
      currentValues.allowMultipleChoices !== initialValues.allowMultipleChoices ||
      JSON.stringify(currentValues.mediaIds) !== JSON.stringify(initialValues.mediaIds) ||
      (categoriesKnown && JSON.stringify(currentValues.categories) !== JSON.stringify(initialValues.categories))
    );
  }, [isEditing, initialValues, content, postType, price, targetAmount, pollQuestion, pollOptions, allowMultipleChoices, mediaData.items, defaultPostType, categories, categoriesKnown]);

  // Helper function to determine final post type based on audience settings
  const determinePostType = () => {
    // TARGET and POLL types remain unchanged
    if (postType === 'TARGET' || postType === 'POLL') {
      return postType;
    }
    
    // If Public is selected, return PUBLIC (overrides other settings)
    if (audienceSettings.isPublic) {
      return 'PUBLIC';
    }
    
    const { visibleToSubscribers, visibleToFollowers } = audienceSettings;
    
    if (visibleToSubscribers && visibleToFollowers) {
      return 'POST'; // Both audiences
    } else if (visibleToSubscribers && !visibleToFollowers) {
      return 'SUBSCRIBERS_ONLY';
    } else if (!visibleToSubscribers && visibleToFollowers) {
      return 'FOLLOWERS_ONLY';
    } else {
      // This shouldn't happen due to validation, but fallback to POST
      return 'POST';
    }
  };

  const validateAudienceSelection = () => {
    // If public is selected, validation passes
    if (audienceSettings.isPublic) {
      return true;
    }
    // For non-public POST type, need at least one audience
    if (postType === 'POST') {
      const { visibleToSubscribers, visibleToFollowers } = audienceSettings;
      if (!visibleToSubscribers && !visibleToFollowers) {
        toast.error('Post must be visible to at least one audience (Subscribers or Followers)');
        return false;
      }
    }
    return true;
  };

  const toggleFormat = (format) => {
    if (!editor) return;
    
    switch (format) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'bulletList':
        editor.chain().focus().toggleBulletList().run();
        break;
      default:
        break;
    }
  };

  // Handle system creator tagging (metadata only, not inserted into content)
  const handleSystemCreatorTag = (user) => {
    if (!user) return;
    
    setSystemTaggedCreators(prev => {
      if (prev.find(u => u.id === user.id)) return prev;
      return [...prev, user];
    });
    // Close the modal
    setShowTagModal(false);
  };

  // Remove a system-tagged creator
  const removeSystemTaggedCreator = (userId) => {
    setSystemTaggedCreators(prev => prev.filter(u => u.id !== userId));
  };

  // Handle media uploader updates
  const handleMediaUpdate = (mediaData, messagePrice) => {
    
    // Handle both old and new data structures
    let items, price;
    
    if (mediaData && typeof mediaData === 'object' && !Array.isArray(mediaData)) {
      // New structure: { mediaItems: [], messagePrice: 0, hasPaidContent: false }
      items = mediaData.mediaItems || [];
      price = mediaData.messagePrice || 0;
    } else if (Array.isArray(mediaData)) {
      // Legacy structure: items array directly
      items = mediaData;
      price = messagePrice || 0;
    } else {
      // Fallback
      items = [];
      price = 0;
    }
    
    setMediaData({ items, price });
    // Don't override the price field automatically, let user control it
  };

  // Handle media uploader errors  
  const handleMediaError = (error) => {
    toast.error(error.message || 'Media upload failed');
  };

  const handlePollOptionChange = (index, value) => {
    setPollOptions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addPollOption = () => {
    setPollOptions(prev => [...prev, '']);
  };

  const removePollOption = (index) => {
    setPollOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting || externalIsSubmitting) {
      return;
    }
    
    const mediaItems = Array.isArray(mediaData.items) ? mediaData.items : [];
    
    // Validate audience selection for POST type
    if (postType === 'POST' && !validateAudienceSelection(audienceSettings)) {
      toast.error('Please select at least one audience option (followers or subscribers)');
      return;
    }
    
    if ((!content.trim() && !mediaItems.length) || 
        (postType === 'POLL' && (pollOptions.filter(opt => opt.trim()).length < 2 || !pollQuestion.trim()))) return;

    // Validate post text against prohibited words
    const filterResult = validateText(content, { isHtml: true });
    if (!filterResult.isClean) {
      toast.error(filterResult.message);
      return;
    }

    // Also validate poll question if applicable
    if (postType === 'POLL' && pollQuestion) {
      const pollFilterResult = validateText(pollQuestion);
      if (!pollFilterResult.isClean) {
        toast.error(pollFilterResult.message);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      
      // Determine the final post type based on audience selection
      const finalPostType = determinePostType(postType, audienceSettings);
      
      // Sanitize content to prevent JSON serialization issues
      let sanitizedContent = content ? content.trim() : '';
      
      // Validate the content for JSON serialization
      if (sanitizedContent) {
        try {
          // Test if the content can be JSON serialized
          JSON.stringify(sanitizedContent);
        } catch (e) {
          console.warn('Content has JSON serialization issues, using fallback...', e);
          // If it fails, use a fallback clean content
          sanitizedContent = sanitizedContent.trim();
        }
      }
      
      // For edit mode, ensure we have some content if it's completely empty
      // Backend might have issues with completely empty content even for updates
      if (isEditing && !sanitizedContent && (!mediaData.items || mediaData.items.length === 0)) {
        sanitizedContent = ' '; // Single space to avoid empty string issues
      }
      
      // Separate new S3 uploads from Upload Library selections
      const allItems = Array.isArray(mediaData.items) ? mediaData.items : [];
      // Build aligned parallel lists — filter together so indices never drift
      const validNewUploads = allItems.filter(item => !item.isLibraryItem && item.s3Key);
      const validLibraryItems = allItems.filter(item => item.isLibraryItem && item.mediaFileId);

      // Create the request object as JSON
      const postRequest = {
        content: sanitizedContent,
        postType: finalPostType,
        isPaidContent: !!price && parseFloat(price) > 0,
        ...(scheduledFor && new Date(scheduledFor) > new Date() ? { scheduledFor: new Date(scheduledFor).toISOString() } : {}),
        // Per-item preview flags, split by type — backend processes uploads and library items independently
        mediaPreviewFlags: validNewUploads.map(item => item.isFreePreview || false),
        existingMediaPreviewFlags: validLibraryItems.map(item => item.isFreePreview || false),
        // Selection-order positions for correct interleaved display when uploads & library items are mixed
        mediaDisplayOrders: validNewUploads.map(item => allItems.findIndex(a => a.id === item.id)),
        existingMediaDisplayOrders: validLibraryItems.map(item => allItems.findIndex(a => a.id === item.id)),
        // Only include s3Key for NEW uploads (not existing media)
        mediaFileKeys: validNewUploads.map(item => item.s3Key),
        // UUIDs of Upload Library items to reuse (already processed & approved)
        existingMediaFileIds: validLibraryItems.map(item => item.mediaFileId),
        // Include media metadata (width, height, duration) for video transcoding aspect ratio preservation
        mediaMetadata: validNewUploads.reduce((acc, item) => {
          const key = item.s3Key;
          if (item.width || item.height || item.duration) {
            acc[key] = {
              width: item.width || null,
              height: item.height || null,
              // Convert milliseconds to seconds (Lambda returns ms, backend expects seconds)
              duration: item.duration ? Math.round(item.duration / 1000) : null
            };
          }
          return acc;
        }, {}),
        taggedCreatorIds: systemTaggedCreators.map(user => user.id), // System creator tagging (separate from @mentions in content)
        ...(categoriesKnown ? { categories } : {})
      };
      
      // For EDITING posts only: include mediaIds (PostMedia UUIDs) for existing media to keep
      // This is more secure than exposing S3 keys - only valid UUIDs are sent
      if (isEditing) {
        postRequest.mediaIds = Array.isArray(mediaData.items) ? mediaData.items
          .map(item => item.id)
          .filter(id => id && typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) : [];
      }

      // Rule 1: Post without price - all content should be free/open
      // Rule 2: Post with price - follow media lock settings
      // Rules 3-6: Subscriber/Follower posts follow same media rules
      // Rule 7: Target Goal - show media when goal is reached
      // Rule 8: Poll - show media normally with poll

      // Add price if specified (for any post type except TARGET and POLL)
      if (price && parseFloat(price) > 0 && postType !== 'TARGET' && postType !== 'POLL') {
        // Validate price is within limits
        const priceValue = parseFloat(price);
        if (priceValue < postMinPrice) {
          toast.error(`Post price must be at least $${postMinPrice.toFixed(2)}`);
          return;
        }
        if (priceValue > postMaxPrice) {
          toast.error(`Post price cannot exceed $${postMaxPrice.toFixed(2)}`);
          return;
        }
        postRequest.price = priceValue;
        postRequest.isPaidContent = true;
        // For paid posts, respect the media lock settings from MediaUploader
        // Require at least one non-preview media item so buyers have something to unlock
        const hasLockableMedia =
          validNewUploads.some(item => !item.isFreePreview) ||
          validLibraryItems.some(item => !item.isFreePreview);
        if (!hasLockableMedia) {
          toast.error('Paid posts must include at least one media item that is not marked as a free preview');
          return;
        }
      } else {
        // Rule 1: For posts without price, ensure all media is marked as free preview
        if (allItems.length > 0) {
          postRequest.mediaPreviewFlags = validNewUploads.map(() => true);
          postRequest.existingMediaPreviewFlags = validLibraryItems.map(() => true);
        }
        postRequest.isPaidContent = false;
      }

      if (postType === 'TARGET' && (!targetAmount || parseFloat(targetAmount) < 1)) {
        toast.error('Target posts must have a valid target amount');
        return;
      } else if (postType === 'TARGET') {
        postRequest.targetAmount = parseFloat(targetAmount);
        // Rule 7: Target Goal - media shown normally (free) until goal is reached
        if (allItems.length > 0) {
          postRequest.mediaPreviewFlags = validNewUploads.map(() => true);
          postRequest.existingMediaPreviewFlags = validLibraryItems.map(() => true);
        }
        postRequest.isPaidContent = false;
      }

      if (postType === 'POLL') {
        if (!pollQuestion.trim()) {
          toast.error('Poll question is required');
          return;
        }
        const validOptions = pollOptions.filter(opt => opt.trim());
        if (validOptions.length < 2) {
          toast.error('Poll must have at least 2 options');
          return;
        }
        postRequest.pollQuestion = pollQuestion.trim();
        postRequest.pollOptions = validOptions;
        postRequest.allowMultipleChoices = allowMultipleChoices;
        // Rule 8: Poll - show media normally with poll question and options
        if (allItems.length > 0) {
          postRequest.mediaPreviewFlags = validNewUploads.map(() => true);
          postRequest.existingMediaPreviewFlags = validLibraryItems.map(() => true);
        }
        postRequest.isPaidContent = false;
      }

      // Add main request as a JSON string in the 'content' part
      
      // Validate JSON serialization before sending
      let jsonContent;
      try {
        jsonContent = JSON.stringify(postRequest);
        
      } catch (jsonError) {
        console.error('❌ JSON serialization error:', jsonError);
        console.error('Problematic postRequest:', postRequest);
        console.error('Content that might be causing issue:', postRequest.content);
        console.error('Content type:', typeof postRequest.content);
        console.error('Content length:', postRequest.content?.length);
        toast.error('Error preparing post data. Please try removing special characters.');
        return;
      }
      
      formData.append('content', jsonContent);

      // Call the appropriate service method
      const resultPost = isEditing 
        ? await feedService.updatePost(initialPost.id, formData)
        : await feedService.createPost(formData);
      
      if (!isEditing) {
        // Reset form including tagged creators and media uploader (only for new posts)
        setContent('');
        editor?.commands.setContent('');
        setPostType('POST');
        setPrice('');
        setPriceError(''); // Clear price validation error
        setTargetAmount('');
        setIsPoll(false);
        setPollOptions(['', '']);
        setPollQuestion('');
        setAllowMultipleChoices(false);
        setSystemTaggedCreators([]);
        setCategories([]);
        setScheduledFor('');
        setShowScheduleModal(false);
        setAudienceSettings({ visibleToFollowers: true, visibleToSubscribers: true });
        
        // Reset media uploader
        if (resetMediaUploader) {
          resetMediaUploader();
        }
        
        // Clear draft after successful post creation
        clearDraft();
      }

      // Notify parent component about the new/updated post
      onPostCreated(resultPost);
      
      // Show success message
      const isScheduled = !isEditing && scheduledFor && new Date(scheduledFor) > new Date();
      const isEditingScheduled = isEditing && resultPost?.processingStatus === 'SCHEDULED';
      toast.success(isEditing ? 'Post updated successfully!' : (isScheduled ? 'Post scheduled!' : 'Post created successfully!'));
      
      // Redirect: scheduled posts go to Planned Releases; all others go to the single post page
      if (isScheduled || isEditingScheduled) {
        navigate(SCHEDULED_PAGE_URL);
      } else {
        navigate(`/post/${resultPost.id}`);
      }
      
    } catch (error) {
      console.error(isEditing ? 'Error updating post:' : 'Error creating post:', error);
      
      // Show specific error messages
      if (error.message.includes('Only creators can create paid content')) {
        toast.error('You need to become a creator to post paid content. Click the warning above to get started!');
        // Optionally show a modal or navigate directly
        // setTimeout(() => navigate('/settings', { state: { activeTab: 'creator' } }), 2000);
      } else if (error.message.includes('Post price must be at least') || error.message.includes('Paid content must have a valid price')) {
        toast.error(error.message);
      } else if (error.message.includes('Post price cannot exceed')) {
        toast.error(error.message);
      } else if (error.message.includes('Target posts must have a valid target amount')) {
        toast.error('Target goals must have a valid amount ($1 minimum)');
      } else if (error.message.includes('Poll question cannot be empty')) {
        toast.error('Poll question is required');
      } else if (error.message.includes('Poll must have at least 2 options')) {
        toast.error('Polls need at least 2 options');
      } else if (error.message.includes('Post content cannot be empty')) {
        toast.error('Post content cannot be empty');
      } else {
        // Generic error message
        toast.error(error.message || 'Failed to create post. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const mediaFiles = files.filter(file => 
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );

    if (mediaFiles.length > 0) {
      setShowMediaUploader(true);
      // The MediaUploader will handle the files via its droppedFiles prop
    }
  };

  // Check if there's any content in the form
  const hasContent = () => {
    const textContent = editor?.getText()?.trim() || '';
    const hasMedia = mediaData?.items?.length > 0;
    const hasPrice = price?.trim();
    const hasTargetAmount = targetAmount?.trim();
    const hasPollContent = pollQuestion?.trim() || pollOptions.some(opt => opt?.trim());
    const hasTaggedCreators = systemTaggedCreators?.length > 0;
    
    return textContent || hasMedia || hasPrice || hasTargetAmount || hasPollContent || hasTaggedCreators;
  };

  // Handle close with confirmation for unsaved drafts
  const handleClose = () => {
    handleCloseWithConfirmation();
  };

  // Get the title based on post type
  const getModalTitle = () => {
    if (isEditing) {
      return postType === 'TARGET' ? 'Edit Goal' : postType === 'POLL' ? 'Edit Poll' : 'Edit Post';
    }
    return postType === 'TARGET' ? 'Create Goal' : postType === 'POLL' ? 'Create Poll' : 'Create Post';
  };

  return (
    <>
    <ComposeBox 
      $isModal={showHeader}
      className={isDragging ? 'dragging' : ''}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Creator Info Banner - Show to non-creators */}
      {!currentUser?.creator && (
        <div style={{ 
          padding: '12px 16px', 
          background: theme.colors.primary || 'rgba(255, 177, 128, 1)',
          borderRadius: '12px',
          marginBottom: '16px',
          fontSize: '14px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease',
          fontWeight: '500'
        }}
        onClick={() => navigate('/settings', { state: { activeTab: 'creator' } })}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = 'none';
        }}
        >
          You need to be a verified creator to post paid content. Click here to become a creator!
        </div>
      )}
      
      {/* Post type label with close button */}
      <div style={{
        fontSize: '16px',
        fontWeight: '600',
        color: theme.colors.primaryDark,
        marginBottom: '16px',
        padding: '8px 16px',
        background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.primary}08)`,
        borderRadius: '8px',
        border: `1px solid ${theme.colors.primary}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <VipIcon 
            name={postType === 'TARGET' ? 'Target' : postType === 'POLL' ? 'CreatePoll' : 'Post'} 
            size={20} 
            color={theme.colors.primaryDark}
          />
          {isEditing ? 'Edit ' : ''}{postType === 'TARGET' ? 'Target Goal' : postType === 'POLL' ? 'Poll' : (postType === 'PUBLIC' ? 'Public Post' : 'Post')}
        </div>
        {showHeader && onRequestClose && (
          <CloseButton type="button" onClick={handleClose} title="Close" aria-label="Close">
            <VipIcon name="CloseXCircle" size={18} />
          </CloseButton>
        )}
      </div>
      
      <Box as="form" onSubmit={handleSubmit}>
        
        {/* Post Type Selection - hidden, using bottom action buttons instead */}
        {/* 
        <div style={{ marginBottom: '16px' }}>
          <PostTypeSelect 
            value={postType}
            onChange={(e) => {
              setPostType(e.target.value);
              setIsPoll(e.target.value === 'POLL');
            }}
            style={{ width: '100%' }}
          >
            <option value="POST">Post</option>
            <option value="TARGET">Target Goal</option>
            <option value="POLL">Poll</option>
          </PostTypeSelect>
        </div>
        */}

        <DragIndicator>Drag and drop media here</DragIndicator>

        <ComposeRow $align="flex-start" $gap="12px">
          <ProfileImage 
            user={currentUser}
            size="40px"
          />
          <ComposeInput>
            <FormatToolbar>
              <FormatButton 
                onClick={() => toggleFormat('bold')}
                $active={editor?.isActive('bold')}
                type="button"
                title="Bold"
              >
                <VipIcon name="Bold" size={20} stroke={theme.colors.primaryDark}/>
              </FormatButton>
              <FormatButton 
                onClick={() => toggleFormat('italic')}
                $active={editor?.isActive('italic')}
                type="button"
                title="Italic"
              >
                <VipIcon name="Italic" size={20} stroke={theme.colors.primaryDark}/>
              </FormatButton>
              <FormatButton 
                onClick={() => toggleFormat('bulletList')}
                $active={editor?.isActive('bulletList')}
                type="button"
                title="Bullet List"
              >
                <VipIcon name="TextList" size={20} stroke={theme.colors.primaryDark}/>
              </FormatButton>
              {/* System Creator Tagging Button - separate from @mentions in text */}
              <FormatButton 
                onClick={() => setShowTagModal(true)}
                $active={showTagModal}
                type="button"
                title="Tag verified creators (system tagging)"
                style={{ color: theme.colors.primaryDark }}
              >
                <VipIcon 
                  name="UserTag" 
                  size={20} 
                  stroke={theme.colors.primaryDark}
                />
              </FormatButton>
              {/* Post categories - opens a picker modal */}
              {categoriesKnown && (
                <FormatButton
                  onClick={() => setShowCategoryModal(true)}
                  $active={showCategoryModal || categories.length > 0}
                  type="button"
                  title={categories.length ? `Categories: ${categories.join(', ')}` : 'Add post categories'}
                  style={{ color: theme.colors.primaryDark, position: 'relative' }}
                >
                  <VipIcon name="PostCategories" size={20} stroke={theme.colors.primaryDark} />
                  {categories.length > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 700, marginLeft: 4 }}>{categories.length}</span>
                  )}
                </FormatButton>
              )}
            </FormatToolbar>
            <EditorContent editor={editor} />
          </ComposeInput>
        </ComposeRow>

        {/* Display System Tagged Creators */}
        <TaggedCreators
          creators={systemTaggedCreators}
          onRemoveCreator={removeSystemTaggedCreator}
        />

        {/* Audience Selection for POST type - Compact with VipIcons */}
        {/* Disabled when editing - visibility cannot be changed after creation */}
        {postType === 'POST' && (
          <AudienceContainer style={{ opacity: isEditing ? 0.6 : 1 }}>
            <AudienceLabel>
              Who can see this post?
              {isEditing && <span style={{ fontSize: '11px', color: theme.colors.textSecondary, marginLeft: '8px' }}>(cannot change)</span>}
              {!isEditing && (
                <Tooltip 
                  title="Audience Options"
                  iconSize={16}
                  iconColor={theme.colors.textSecondary}
                  content={
                    <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
                      <p style={{ marginBottom: '12px' }}>
                        <strong>Public:</strong> Visible to everyone, even without an account. Public posts are always free (no paid content allowed) and limited to 300MB total media size.
                      </p>
                      <p style={{ marginBottom: '12px' }}>
                        <strong>Followers:</strong> Only users who follow you can see this post.
                      </p>
                      <p>
                        <strong>Subscribers:</strong> Only your paid subscribers can see this post.
                      </p>
                    </div>
                  }
                />
              )}
            </AudienceLabel>
            <AudienceOptionsContainer style={{ pointerEvents: isEditing ? 'none' : 'auto' }}>
              <AudienceOption 
                onClick={() => {
                  if (isEditing) return;
                  setAudienceSettings(prev => ({
                    ...prev,
                    isPublic: !prev.isPublic,
                    // When public is selected, uncheck followers/subscribers
                    visibleToFollowers: !prev.isPublic ? false : prev.visibleToFollowers,
                    visibleToSubscribers: !prev.isPublic ? false : prev.visibleToSubscribers
                  }));
                  // When public is selected, reset price to empty
                  if (!audienceSettings.isPublic) {
                    setPrice('');
                    setPriceError('');
                    setShowPriceSection(false);
                  }
                }}
              >
                <VipIcon 
                  name={audienceSettings.isPublic ? "CheckmarkCircle" : "CircleOutline"} 
                  size={18} 
                  color={audienceSettings.isPublic ? theme.colors.primary : theme.colors.textSecondary}
                />
                Public
              </AudienceOption>
              <AudienceOption 
                onClick={() => {
                  if (isEditing) return;
                  setAudienceSettings(prev => ({
                    ...prev,
                    visibleToFollowers: !prev.visibleToFollowers,
                    // When selecting followers, uncheck public
                    isPublic: !prev.visibleToFollowers ? false : prev.isPublic
                  }));
                }}
              >
                <VipIcon 
                  name={audienceSettings.visibleToFollowers ? "CheckmarkCircle" : "CircleOutline"} 
                  size={18} 
                  color={audienceSettings.visibleToFollowers ? theme.colors.primary : theme.colors.textSecondary}
                />
                Followers
              </AudienceOption>
              <AudienceOption 
                onClick={() => {
                  if (isEditing) return;
                  setAudienceSettings(prev => ({
                    ...prev,
                    visibleToSubscribers: !prev.visibleToSubscribers,
                    // When selecting subscribers, uncheck public
                    isPublic: !prev.visibleToSubscribers ? false : prev.isPublic
                  }));
                }}
              >
                <VipIcon 
                  name={audienceSettings.visibleToSubscribers ? "CheckmarkCircle" : "CircleOutline"} 
                  size={18} 
                  color={audienceSettings.visibleToSubscribers ? theme.colors.primary : theme.colors.textSecondary}
                />
                Subscribers
              </AudienceOption>
            </AudienceOptionsContainer>
            {!isEditing && !audienceSettings.isPublic && !audienceSettings.visibleToFollowers && !audienceSettings.visibleToSubscribers && (
              <ValidationMessage>
                (Select at least one)
              </ValidationMessage>
            )}
          </AudienceContainer>
        )}

        {/* Price and Target Amount inputs - Compact (hidden for PUBLIC posts) */}
        {(postType !== 'POLL') && (postType === 'TARGET' || (!audienceSettings.isPublic && showPriceSection)) && (
          <div style={{ 
            marginTop: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{ 
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.primaryDark,
              minWidth: 'fit-content',
              paddingTop: '10px'
            }}>
              {postType === 'TARGET' ? 'Goal:' : 'Price:'}
            </div>
            <div style={{ flex: 1 }}>
              <Input
                type="number"
                placeholder={postType === 'TARGET' ? '1000' : postMinPrice.toFixed(2)}
                value={postType === 'TARGET' ? targetAmount : price}
                onChange={(e) => {
                  const newPrice = e.target.value;
                  console.log('💰 [CreatePost] Price input changed:', { from: price, to: newPrice });
                  if (postType === 'TARGET') {
                    setTargetAmount(newPrice);
                  } else {
                    handlePriceChange(newPrice);
                    console.log('💰 [CreatePost] Price state updated, should trigger MediaUploader sync');
                  }
                }}
                min={postType === 'TARGET' ? '1' : postMinPrice.toString()}
                step={postType === 'TARGET' ? '1' : '0.01'}
                icon={<span style={{ fontSize: '14px', color: theme.colors.textSecondary }}>$</span>}
                error={postType !== 'TARGET' ? priceError : null}
                marginBottom="0"
                rounded
              />
            </div>
          </div>
        )}

        {/* Poll-specific UI */}
        {postType === 'POLL' && (
          <div style={{ marginTop: '16px' }}>
            <input
              type="text"
              placeholder="Poll question"
              value={pollQuestion}
              onChange={(e) => setPollQuestion(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${theme.colors.divider}`,
                borderRadius: '8px',
                marginBottom: '12px',
                fontSize: '14px'
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {pollOptions.map((option, index) => (
                <PollOption key={index}>
                  <input
                    type="text"
                    placeholder={`Option ${index + 1}`}
                    value={option}
                    onChange={(e) => handlePollOptionChange(index, e.target.value)}
                  />
                  {index > 1 && (
                    <UnstyledButton type="button" onClick={() => removePollOption(index)}>
                      <VipIcon name="CloseXCircle" size={16} stroke={theme.colors.error}/>
                    </UnstyledButton>
                  )}
                </PollOption>
              ))}
              {pollOptions.length < 10 && (
                <AddOptionButton type="button" onClick={addPollOption}>
                  <VipIcon name="Plus" size={16} stroke={theme.colors.primary}/>
                  Add Option
                </AddOptionButton>
              )}
            </div>
            <div style={{ marginTop: '12px' }}>
              <div 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: theme.colors.primaryDark
                }}
                onClick={() => setAllowMultipleChoices(prev => !prev)}
              >
                <VipIcon 
                  name={allowMultipleChoices ? "CheckmarkCircle" : "CircleOutline"} 
                  size={18} 
                  color={allowMultipleChoices ? theme.colors.primary : theme.colors.textSecondary}
                />
                Allow multiple choices
              </div>
            </div>
          </div>
        )}

        {/* Media Uploader - using the same component as messages */}
        <MediaUploader
          onMediaUpdate={handleMediaUpdate}
          onError={handleMediaError}
          maxFiles={10}
          allowedTypes={['image/*', 'video/*']}
          theme={theme}
          triggerUpload={triggerFileUpload}
          onTriggerReset={() => setTriggerFileUpload(false)}
          triggerLibrary={triggerLibrary}
          onTriggerLibraryReset={() => setTriggerLibrary(false)}
          onShowUploader={() => setShowMediaUploader(true)}
          isVisible={showMediaUploader || (Array.isArray(mediaData.items) && mediaData.items.length > 0)}
          onReset={(resetFn) => setResetMediaUploader(() => resetFn)}
          initialPrice={price}
          postType={postType}
          context="post"  // Specify this is for post uploads
          isEditing={isEditing}
          initialMedia={isEditing ? formatMediaForCarousel(initialPost?.media || []) : (draftLoaded ? mediaData.items : [])}
        />



        {/* Scheduled time pill — sits just above the action bar */}
        {scheduledFor && !isEditing && (
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px 2px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              background: `${theme.colors.primary}18`,
              borderRadius: '20px',
              padding: '4px 6px 4px 10px',
            }}>
              <UnstyledButton
                type="button"
                onClick={() => setShowScheduleModal(true)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <VipIcon name="Clock" size={12} color={theme.colors.primary} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.primary, whiteSpace: 'nowrap' }}>
                  Will post on {new Date(scheduledFor).toLocaleString([], {
                    month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })}
                </span>
              </UnstyledButton>
              <UnstyledButton
                type="button"
                onClick={() => setScheduledFor('')}
                style={{ background: 'none', border: 'none', padding: '0 0 0 2px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: theme.colors.primary, opacity: 0.7 }}
                title="Clear scheduled time"
              >
                <VipIcon name="CloseXCircle" size={13} />
              </UnstyledButton>
            </div>
          </div>
        )}

        <ComposeActions>
          <MediaButtons>
            <UploadGroup>
              <MediaButton
                type="button"
                onClick={() => setTriggerFileUpload(true)}
                title="Add photos/videos"
              >
                <VipIcon name="Paperclip" size={25} />
              </MediaButton>

              <MediaButton
                type="button"
                onClick={() => setTriggerLibrary(true)}
                title="Upload Library — reuse previously uploaded media"
              >
                <VipIcon name="Gallery" size={25} />
              </MediaButton>
            </UploadGroup>
            
            {/* Post type switching buttons - hidden when editing (post type cannot be changed) */}
            {!isEditing && postType === 'POST' && (
              <>
                <MediaButton
                  type="button"
                  onClick={() => {
                    setPostType('POLL');
                    setIsPoll(true);
                  }}
                  title="Switch to Poll mode"
                >
                  <VipIcon name="CreatePoll" size={25} />
                </MediaButton>
                {hasAccess('canCreatePaidContent') && (
                  <MediaButton
                    type="button"
                    onClick={() => {
                      setPostType('TARGET');
                      setIsPoll(false);
                    }}
                    title="Switch to Target Goal mode"
                  >
                    <VipIcon name="Target" size={25} />
                  </MediaButton>
                )}
              </>
            )}
            
            {!isEditing && postType === 'POLL' && (
              <>
                <MediaButton
                  type="button"
                  onClick={() => {
                    setPostType('POST');
                    setIsPoll(false);
                  }}
                  title="Switch to Regular Post mode"
                >
                  <VipIcon name="Post" size={25} />
                </MediaButton>
                {hasAccess('canCreatePaidContent') && (
                  <MediaButton
                    type="button"
                    onClick={() => {
                      setPostType('TARGET');
                      setIsPoll(false);
                    }}
                    title="Switch to Target Goal mode"
                  >
                    <VipIcon name="Target" size={25} />
                  </MediaButton>
                )}
              </>
            )}
            
            {!isEditing && postType === 'TARGET' && (
              <>
                <MediaButton
                  type="button"
                  onClick={() => {
                    setPostType('POST');
                    setIsPoll(false);
                  }}
                  title="Switch to Regular Post mode"
                >
                  <VipIcon name="Post" size={25} />
                </MediaButton>
                <MediaButton
                  type="button"
                  onClick={() => {
                    setPostType('POLL');
                    setIsPoll(true);
                  }}
                  title="Switch to Poll mode"
                >
                  <VipIcon name="CreatePoll" size={25} />
                </MediaButton>
              </>
            )}
            
            {/* Price/Goal toggle button - only for POST type (not POLL, TARGET, or PUBLIC) and if user can create paid content */}
            {postType === 'POST' && !audienceSettings.isPublic && hasAccess('canCreatePaidContent') && (
              <MediaButton
                type="button"
                onClick={() => setShowPriceSection(prev => !prev)}
                title="Set price"
                className={showPriceSection ? 'active' : ''}
              >
                <VipIcon name="DollarSign" size={25} />
              </MediaButton>
            )}

            {/* Schedule button - only for new posts by creators */}
            {!isEditing && currentUser?.creator && (
              <MediaButton
                type="button"
                onClick={() => setShowScheduleModal(true)}
                title={scheduledFor ? `Scheduled: ${new Date(scheduledFor).toLocaleString()}` : 'Schedule post'}
                className={showScheduleModal || scheduledFor ? 'active' : ''}
              >
                <VipIcon name="Clock" size={25} />
              </MediaButton>
            )}
          </MediaButtons>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Save to Draft icon button - only for new posts */}
            {!isEditing && hasUnsavedContent && (
              <MediaButton
                type="button"
                onClick={handleSaveDraft}
                disabled={draftSaved}
                title={draftSaved ? 'Draft saved' : 'Save as draft'}
                style={{ 
                  opacity: draftSaved ? 0.5 : 1,
                  color: draftSaved ? theme.colors?.success : theme.colors?.text
                }}
              >
                <VipIcon name={draftSaved ? 'CheckmarkCircle' : 'DraftIcon'} size={25} />
              </MediaButton>
            )}

            <ComposeButton
              type="submit"
              disabled={(externalIsSubmitting || isSubmitting) || 
                       (!editor?.getText().trim() && !(Array.isArray(mediaData.items) && mediaData.items.length > 0)) || 
                       (isPoll && pollOptions.filter(opt => opt.trim()).length < 2) ||
                       (isEditing && !hasChanges) ||
                       (priceError && price) ||
                       (price && parseFloat(price) > 0 && !(Array.isArray(mediaData.items) && mediaData.items.length > 0))}
            >
              {(externalIsSubmitting || isSubmitting) 
                ? (isEditing ? 'Updating...' : (scheduledFor && new Date(scheduledFor) > new Date() ? 'Scheduling...' : 'Posting...')) 
                : isEditing && !hasChanges 
                    ? 'No Changes' 
                    : scheduledFor && !isEditing && new Date(scheduledFor) > new Date()
                        ? 'Schedule'
                        : submitButtonText}
            </ComposeButton>
          </div>
        </ComposeActions>
      </Box>

      {selectedMedia && (
        <MediaModal
          isOpen={!!selectedMedia}
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}

      <CategoryPickerModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        value={categories}
        onChange={setCategories}
      />

      {/* Tag Creator Modal */}
      {showTagModal && (
        <UserTaggingModal
          isOpen={showTagModal}
          onClose={() => setShowTagModal(false)}
          onUserSelect={handleSystemCreatorTag}
          currentUser={currentUser}
        />
      )}
    </ComposeBox>

    {/* Discard Draft Confirmation Modal - Outside ComposeBox to avoid z-index issues */}
    <ConfirmModal
      isOpen={showDiscardModal}
      onClose={() => setShowDiscardModal(false)}
      onConfirm={handleDiscardConfirm}
      title="Discard Post?"
      message="You have unsaved changes. Are you sure you want to discard this post?"
      confirmText="Discard"
      cancelText="Keep Editing"
      danger
    />
    <DateTimePickerModal
      isOpen={showScheduleModal}
      onClose={() => setShowScheduleModal(false)}
      onConfirm={(val) => setScheduledFor(val)}
      onClear={() => setScheduledFor('')}
      value={scheduledFor}
      title="Schedule Post"
    />
    </>
  );
};

export default CreatePost;